<template>
  <div class="cc-home">
    <AppNavigation />

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-background">
        <div class="gradient-orb orb-1"></div>
      </div>
      
      <v-container class="hero-content py-16">
        <v-row align="center" justify="center">
          <v-col cols="12" md="6" class="text-center text-md-left pr-md-8">
            <div class="hero-eyebrow mb-6" :class="{ 'animate-in': mounted }">
              <v-icon size="16" color="#F0D080">mdi-image-multiple</v-icon>
              <span>Employees &middot; Managers &middot; HR Teams &middot; Admins</span>
            </div>
            
            <h1 class="hero-title mb-6" :class="{ 'slide-in-left': mounted }">
              Share Company Culture. Stay Connected. Build Community.
            </h1>
            
            <p class="hero-subtitle mb-8" :class="{ 'slide-in-left-delayed': mounted }">
              Corporate Connectionz is a social platform built just for companies — share recognition and celebrations from company life, join department and interest groups, message your team, and give leadership a real window into company culture.
            </p>
            
            <div class="cta-buttons mb-6" :class="{ 'fade-in-up': mounted }">
              <v-btn
                x-large
                depressed
                class="mr-4 mb-3 cta-btn-primary btn-gold"
                to="/demo"
              >
                <v-icon left size="22" color="#08090A">mdi-rocket-launch</v-icon>
                Get Started Free
              </v-btn>
            </div>
            
            <div class="trust-indicators" :class="{ 'fade-in': mounted }">
              <div class="trust-badge">
                <v-icon color="white" small>mdi-shield-check</v-icon>
                <span>Enterprise-Grade Security</span>
              </div>
              <div class="trust-badge">
                <v-icon color="white" small>mdi-cloud-check</v-icon>
                <span>99.9% Uptime</span>
              </div>
            </div>
          </v-col>
          
          <v-col cols="12" md="6" class="text-center">
            <div class="phone-mockup-container" :class="{ 'float-animation': mounted }">
              <div class="mockup-glow"></div>
              <div class="phone-frame">
                <div class="phone-screen">
                  <!-- Status Bar -->
                  <div class="status-bar">
                    <span class="time">9:41</span>
                    <div class="notch"></div>
                    <div class="status-icons">
                      <v-icon x-small color="white">mdi-signal</v-icon>
                      <v-icon x-small color="white">mdi-wifi</v-icon>
                      <v-icon x-small color="white">mdi-battery</v-icon>
                    </div>
                  </div>
                  
                  <!-- App Header -->
                  <div class="app-header">
                    <v-icon color="white" size="20">mdi-menu</v-icon>
                    <div class="header-logo">
                      <v-icon color="white" size="16">mdi-account-group</v-icon>
                      <span>Corporate Connectionz</span>
                    </div>
                    <div class="header-actions">
                      <v-icon color="white" size="20">mdi-bell</v-icon>
                      <div class="notification-dot"></div>
                    </div>
                  </div>
                  
                  <!-- Content Area -->
                  <div class="screen-content">
                    <!-- Story Circles -->
                    <div class="stories-row">
                      <div class="story-item">
                        <div class="story-ring">
                          <div class="story-avatar" style="background: linear-gradient(135deg, #F0D080 0%, #C9A84C 100%);">
                            <v-icon color="white" size="16">mdi-chart-line</v-icon>
                          </div>
                        </div>
                        <span>Wins</span>
                      </div>
                      <div class="story-item">
                        <div class="story-ring">
                          <div class="story-avatar" style="background: linear-gradient(135deg, #9C7A34 0%, #6B5423 100%);">
                            <v-icon color="white" size="16">mdi-cake-variant</v-icon>
                          </div>
                        </div>
                        <span>Birthdays</span>
                      </div>
                      <div class="story-item">
                        <div class="story-ring">
                          <div class="story-avatar" style="background: linear-gradient(135deg, #6A6D74 0%, #3A3D44 100%);">
                            <v-icon color="white" size="16">mdi-medal</v-icon>
                          </div>
                        </div>
                        <span>Kudos</span>
                      </div>
                      <div class="story-item">
                        <div class="story-ring">
                          <div class="story-avatar" style="background: linear-gradient(135deg, #4A4D54 0%, #2C2E33 100%);">
                            <v-icon color="white" size="16">mdi-calendar-star</v-icon>
                          </div>
                        </div>
                        <span>Events</span>
                      </div>
                    </div>
                    
                    <!-- Feed Post -->
                    <div class="feed-post">
                      <div class="post-header">
                        <div class="user-info">
                          <div class="user-avatar">
                            <v-icon color="white" size="18">mdi-account-tie</v-icon>
                          </div>
                          <div class="user-details">
                            <div class="user-name">Company Updates</div>
                            <div class="post-meta">
                              <v-icon x-small>mdi-clock-outline</v-icon>
                              <span>2 hours ago</span>
                              <span class="dot">•</span>
                              <v-icon x-small>mdi-earth</v-icon>
                            </div>
                          </div>
                        </div>
                        <v-icon size="18">mdi-dots-horizontal</v-icon>
                      </div>
                      
                      <div class="post-content">
                        <p>Huge shoutout to the sales team for crushing Q4! 🎉 Your dedication inspires us all! 🚀</p>
                      </div>
                      
                      <div class="post-image">
                        <div class="image-placeholder">
                          <div class="image-grid">
                            <div class="grid-item" style="background: linear-gradient(135deg, #F0D080 0%, #C9A84C 100%);">
                              <v-icon color="white" size="30">mdi-chart-line</v-icon>
                            </div>
                            <div class="grid-item" style="background: linear-gradient(135deg, #6A6D74 0%, #3A3D44 100%);">
                              <v-icon color="white" size="30">mdi-image</v-icon>
                            </div>
                            <div class="grid-item" style="background: linear-gradient(135deg, #9C7A34 0%, #6B5423 100%);">
                              <v-icon color="white" size="30">mdi-heart</v-icon>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div class="post-stats">
                        <div class="stat-item">
                          <div class="reaction-icons">
                            <div class="reaction-bubble" style="background: #C9A84C;">👍</div>
                            <div class="reaction-bubble" style="background: #6A6D74;">❤️</div>
                            <div class="reaction-bubble" style="background: #9C7A34;">🎉</div>
                          </div>
                          <span>142 reactions</span>
                        </div>
                        <div class="stat-item">
                          <span>35 comments</span>
                        </div>
                      </div>
                      
                      <div class="post-actions">
                        <div class="action-btn active">
                          <v-icon size="18" color="#C9A84C">mdi-thumb-up</v-icon>
                          <span>Like</span>
                        </div>
                        <div class="action-btn">
                          <v-icon size="18">mdi-comment-outline</v-icon>
                          <span>Comment</span>
                        </div>
                        <div class="action-btn">
                          <v-icon size="18">mdi-share-variant</v-icon>
                          <span>Share</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Bottom Navigation -->
                  <div class="bottom-nav">
                    <div class="nav-item active">
                      <v-icon color="#C9A84C" size="22">mdi-home</v-icon>
                      <div class="nav-indicator"></div>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-calendar-month</v-icon>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-image-multiple</v-icon>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-message</v-icon>
                      <div class="message-badge">5</div>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-account-circle</v-icon>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Features Section -->
    <section id="features" class="features-section">
      <v-container>
        <div class="section-header text-center mb-12">
          <div class="section-badge mb-4" :class="{ 'fade-in-up': featuresVisible }">
            <v-icon small color="#C9A84C">mdi-star-four-points</v-icon>
            <span>ONE COMPANY COMMUNITY</span>
          </div>
          <h2 class="section-title mb-4" :class="{ 'fade-in-up': featuresVisible }">
            Everything Your Company Needs
          </h2>
          <p class="section-subtitle" :class="{ 'fade-in-up-delayed': featuresVisible }">
            A social platform built for employees, managers, HR teams, and admins to share, connect, and stay in the loop — all in one place
          </p>
        </div>
        
        <v-row class="mb-12">
          <v-col
            v-for="(feature, index) in features"
            :key="index"
            cols="12"
            md="6"
            lg="4"
            class="mb-6"
          >
            <div
              class="feature-card-modern"
              :class="{ 'card-animate-in': featuresVisible }"
              :style="{ 'animation-delay': `${index * 0.1}s` }"
            >
              <div class="feature-card-glow" :style="{ background: feature.gradient }"></div>
              <div class="feature-card-content">
                <div class="feature-icon-modern" :style="{ background: feature.gradient }">
                  <v-icon size="32" color="white">{{ feature.icon }}</v-icon>
                  <div class="icon-ring"></div>
                </div>
                
                <h3 class="feature-title">{{ feature.title }}</h3>
                <p class="feature-description">{{ feature.description }}</p>
                
                <div class="feature-benefits">
                  <div v-for="(benefit, idx) in feature.benefits" :key="idx" class="benefit-item">
                    <v-icon size="14" color="#F0D080">mdi-check-circle</v-icon>
                    <span>{{ benefit }}</span>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Interactive Demo Section -->
    <section id="demo" class="demo-section">
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="6" order="2" order-md="1">
            <div class="demo-interface" :class="{ 'slide-in-left': demoVisible }">
              <div class="demo-tabs">
                <div
                  v-for="(tab, index) in demoTabs"
                  :key="index"
                  class="demo-tab"
                  :class="{ active: activeTab === index }"
                  @click="activeTab = index"
                >
                  <v-icon size="20">{{ tab.icon }}</v-icon>
                  <span>{{ tab.label }}</span>
                </div>
              </div>
              
              <div class="demo-content">
                <transition name="fade" mode="out-in">
                  <div :key="activeTab" class="demo-panel">
                    <div class="demo-visual" :style="{ background: demoTabs[activeTab].gradient }">
                      <v-icon size="80" color="white">{{ demoTabs[activeTab].icon }}</v-icon>
                      <div class="demo-float-elements">
                        <div v-for="i in 5" :key="i" class="float-element" :style="getFloatStyle(i)"></div>
                      </div>
                    </div>
                    <h4 class="demo-panel-title">{{ demoTabs[activeTab].title }}</h4>
                    <p class="demo-panel-text">{{ demoTabs[activeTab].description }}</p>
                    
                    <div class="demo-features-list">
                      <div v-for="(item, idx) in demoTabs[activeTab].items" :key="idx" class="demo-feature-item">
                        <div class="feature-number">{{ idx + 1 }}</div>
                        <div class="feature-text">{{ item }}</div>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </v-col>
          
          <v-col cols="12" md="6" order="1" order-md="2">
            <div class="demo-info" :class="{ 'slide-in-right': demoVisible }">
              <div class="section-badge mb-4">
                <v-icon small color="#C9A84C">mdi-creation</v-icon>
                <span>POWERFUL FEATURES</span>
              </div>
              
              <h2 class="section-title mb-4">
                Designed Around Real Company Life
              </h2>
              
              <p class="section-text mb-8">
                Our platform combines intuitive design with powerful functionality so everyone at your company actually wants to use it every day.
              </p>
              
              <div class="info-stats">
                <div v-for="(stat, index) in platformStats" :key="index" class="stat-item-modern">
                  <div class="stat-icon" :style="{ background: stat.gradient }">
                    <v-icon color="white" size="24">{{ stat.icon }}</v-icon>
                  </div>
                  <div class="stat-details">
                    <div class="stat-value">{{ stat.value }}</div>
                    <div class="stat-label">{{ stat.label }}</div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Benefits Section -->
    <section class="benefits-section">
      <div class="benefits-background">
        <div class="benefit-shape shape-1"></div>
        <div class="benefit-shape shape-2"></div>
        <div class="benefit-shape shape-3"></div>
      </div>
      
      <v-container>
        <div class="section-header text-center mb-12">
          <div class="section-badge mb-4" :class="{ 'fade-in-up': benefitsVisible }">
            <v-icon small color="white">mdi-shield-star</v-icon>
            <span>WHY CHOOSE US</span>
          </div>
          <h2 class="section-title white--text mb-4" :class="{ 'fade-in-up': benefitsVisible }">
            Built to Bring Your Company Closer Together
          </h2>
          <p class="section-subtitle white--text" :class="{ 'fade-in-up-delayed': benefitsVisible }">
            A social space where your whole company can actually connect — not just another notice board
          </p>
        </div>
        
        <v-row>
          <v-col
            v-for="(benefit, index) in coreBenefits"
            :key="index"
            cols="12"
            md="6"
            class="mb-6"
          >
            <div
              class="benefit-card"
              :class="{ 'card-animate-in': benefitsVisible }"
              :style="{ 'animation-delay': `${index * 0.15}s` }"
            >
              <div class="benefit-card-inner">
                <div class="benefit-icon-section">
                  <div class="benefit-icon-wrap" :style="{ background: benefit.gradient }">
                    <v-icon size="36" color="white">{{ benefit.icon }}</v-icon>
                  </div>
                  <div class="benefit-badge">{{ benefit.badge }}</div>
                </div>
                
                <div class="benefit-content-section">
                  <h3 class="benefit-title">{{ benefit.title }}</h3>
                  <p class="benefit-text">{{ benefit.description }}</p>
                  
                  <div class="benefit-points">
                    <div v-for="(point, idx) in benefit.points" :key="idx" class="point-item">
                      <div class="point-dot"></div>
                      <span>{{ point }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Technology Section -->
    <section class="technology-section">
      <v-container>
        <div class="section-header text-center mb-12">
          <div class="section-badge mb-4">
            <v-icon small color="#C9A84C">mdi-chip</v-icon>
            <span>ENTERPRISE TECHNOLOGY</span>
          </div>
          <h2 class="section-title mb-4">
            Built on Modern, Secure Infrastructure
          </h2>
          <p class="section-subtitle">
            Data security and privacy are our top priorities - enterprise-grade from the ground up
          </p>
        </div>
        
        <v-row>
          <v-col
            v-for="(tech, index) in techFeatures"
            :key="index"
            cols="12"
            sm="6"
            md="3"
            class="mb-4"
          >
            <div class="tech-card">
              <div class="tech-icon" :style="{ background: tech.color }">
                <v-icon color="white" size="28">{{ tech.icon }}</v-icon>
              </div>
              <h4 class="tech-title">{{ tech.title }}</h4>
              <p class="tech-description">{{ tech.description }}</p>
            </div>
          </v-col>
        </v-row>
        
        <div class="tech-certifications mt-12">
          <div class="cert-item">
            <v-icon size="32" color="#C9A84C">mdi-lock-check</v-icon>
            <span>AES-256 Encryption</span>
          </div>
          <div class="cert-item">
            <v-icon size="32" color="#C9A84C">mdi-shield-check</v-icon>
            <span>Enterprise-Grade Security</span>
          </div>
        </div>
      </v-container>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="pricing-section">
      <v-container>
        <div class="section-header text-center mb-12">
          <div class="section-badge mb-4" :class="{ 'fade-in-up': pricingVisible }">
            <v-icon small color="#C9A84C">mdi-currency-usd</v-icon>
            <span>TRANSPARENT PRICING</span>
          </div>
          <h2 class="section-title mb-4" :class="{ 'fade-in-up': pricingVisible }">
            One Flat Plan for Every Company
          </h2>
          <p class="section-subtitle" :class="{ 'fade-in-up-delayed': pricingVisible }">
            No per-employee fees, no tiers to choose — one price, everything included
          </p>
        </div>
        
        <v-row justify="center">
          <v-col cols="12" xl="11">
            <div class="pricing-card-premium" :class="{ 'scale-in': pricingVisible }">
              <div class="pricing-badge">
                <v-icon small color="#08090A">mdi-star</v-icon>
                <span>ALL COMPANIES</span>
              </div>

              <div class="pricing-layout">
                <!-- Summary column: price + CTA -->
                <div class="pricing-summary">
                  <div class="pricing-icon">
                    <v-icon size="40" color="white">mdi-account-group</v-icon>
                  </div>
                  <h3 class="pricing-plan-name">Corporate Connectionz Platform</h3>
                  <p class="pricing-tagline">Everything your company needs to build culture</p>

                  <div class="pricing-amount-container">
                    <div class="pricing-amount-large">
                      <span class="currency">$</span>
                      <span class="amount">299<span class="cents">.99</span></span>
                    </div>
                    <div class="pricing-period-label">Per Company / Per Month</div>
                    <p class="pricing-note">Billed monthly • Cancel anytime • Custom pricing available for enterprise</p>
                  </div>

                  <v-btn
                    x-large
                    block
                    depressed
                    class="mb-4 btn-gold"
                    style="text-transform: none; font-size: 16px; padding: 28px;"
                    to="/demo"
                  >
                    <v-icon left size="22" color="#08090A">mdi-calendar-check</v-icon>
                    Schedule Your Demo
                  </v-btn>

                  <div class="pricing-guarantees">
                    <div class="guarantee-item">
                      <v-icon small color="#F0D080">mdi-check-decagram</v-icon>
                      <span>Your first month is free</span>
                    </div>
                    <div class="guarantee-item">
                      <v-icon small color="#F0D080">mdi-check-decagram</v-icon>
                      <span>Free setup & onboarding</span>
                    </div>
                    <div class="guarantee-item">
                      <v-icon small color="#F0D080">mdi-check-decagram</v-icon>
                      <span>No long-term contracts</span>
                    </div>
                  </div>
                </div>

                <!-- Features column: compact multi-column grid -->
                <div class="pricing-features-panel">
                  <div class="feature-groups">
                    <div v-for="(group, index) in featureGroups" :key="index" class="feature-group">
                      <div class="group-header">
                        <v-icon color="#C9A84C" size="18">{{ group.icon }}</v-icon>
                        <h4>{{ group.title }}</h4>
                      </div>
                      <div class="group-features">
                        <div
                          v-for="(feature, idx) in group.features"
                          :key="idx"
                          class="feature-row"
                          :title="feature.detail || ''"
                        >
                          <v-icon color="#F0D080" size="16">mdi-check-circle</v-icon>
                          <span class="feature-name">{{ feature.name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- CTA Section -->
    <section class="final-cta-section">
      <div class="cta-background">
        <div class="cta-orb cta-orb-1"></div>
        <div class="cta-orb cta-orb-2"></div>
        <div class="cta-pattern"></div>
      </div>
      
      <v-container class="text-center">
        <div class="cta-content">
          <v-icon size="64" color="#F0D080" class="mb-6">mdi-rocket-launch</v-icon>
          <h2 class="cta-title white--text mb-4">
            Ready to Bring Your Company Closer Together?
          </h2>
          <p class="cta-subtitle mb-8">
            Give your whole company one place to connect, recognize each other, and stay in the loop.
          </p>
          
          <div class="cta-buttons-final">
            <v-btn
              x-large
              depressed
              class="mr-4 mb-3 btn-gold"
              style="text-transform: none; font-size: 16px; padding: 24px 44px;"
              to="/demo"
            >
              <v-icon left size="22" color="#08090A">mdi-email</v-icon>
              Get Started Now
            </v-btn>
          </div>
        </div>
      </v-container>
    </section>

    <AppFooter />
  </div>
</template>

<script>
  import AppNavigation from '@/components/AppNavigation.vue'
  import AppFooter from '@/components/AppFooter.vue'

  export default {
    name: 'CorporateHome',
    components: { AppNavigation, AppFooter },
    
    data: () => ({
      mounted: false,
      featuresVisible: false,
      demoVisible: false,
      benefitsVisible: false,
      pricingVisible: false,
      activeTab: 0,
      
      features: [
        {
          icon: 'mdi-image-multiple',
          gradient: 'linear-gradient(135deg, #F0D080 0%, #C9A84C 100%)',
          title: 'Company Moments',
          description: 'The heart of Corporate Connectionz — share photos and videos from company life, wins, and everyday moments with your team.',
          benefits: [
            'Photo and video posts with likes and comments',
            'Share publicly or with your groups only',
            'A real feed of what\'s happening at your company'
          ]
        },
        {
          icon: 'mdi-medal-outline',
          gradient: 'linear-gradient(135deg, #C9A84C 0%, #9C7A34 100%)',
          title: 'Recognition & Kudos',
          description: 'Recognize any colleague across categories like Leadership, Innovation, Teamwork, Culture, Excellence, and Mentorship.',
          benefits: [
            'Recognitions post instantly to a company-wide feed',
            'Colleagues can like and comment on every recognition',
            'A dedicated Recognition tab to see who\'s being celebrated'
          ]
        },
        {
          icon: 'mdi-cake-variant-outline',
          gradient: 'linear-gradient(135deg, #E0C27A 0%, #B8923A 100%)',
          title: 'Celebrations & Milestones',
          description: 'Birthdays, work anniversaries, and custom milestones, surfaced automatically so nothing gets missed.',
          benefits: [
            'Birthdays and anniversaries detected automatically',
            'Add custom milestones for promotions and wins',
            'A dedicated Celebrations tab for the whole company'
          ]
        },
        {
          icon: 'mdi-account-group',
          gradient: 'linear-gradient(135deg, #6A6D74 0%, #3A3D44 100%)',
          title: 'Groups & Communities',
          description: 'Create or join public and private groups for departments, projects, and shared interests, with their own posts and cover photos.',
          benefits: [
            'Public groups anyone at the company can discover',
            'Private groups for closer-knit teams',
            'Group posts and member management'
          ]
        },
        {
          icon: 'mdi-account-multiple',
          gradient: 'linear-gradient(135deg, #9C7A34 0%, #6B5423 100%)',
          title: 'Messaging & Connections',
          description: 'Message colleagues directly, and find new people to connect with across your company.',
          benefits: [
            'Direct messaging for your whole company',
            'Smart suggestions to make new connections',
            'Search and browse your entire company'
          ]
        },
        {
          icon: 'mdi-bullhorn-outline',
          gradient: 'linear-gradient(135deg, #D4AF5A 0%, #A6822E 100%)',
          title: 'Announcements & Digests',
          description: 'Company-wide announcements plus scheduled digests that summarize what you missed.',
          benefits: [
            'Pinned announcements targeted by audience',
            'Weekly digest of recognition, news, and events',
            'Revisit any past digest anytime'
          ]
        },
        {
          icon: 'mdi-calendar-star',
          gradient: 'linear-gradient(135deg, #4A4D54 0%, #2C2E33 100%)',
          title: 'Company Calendar & Events',
          description: 'Every event — All Hands, workshops, socials, and personal — in one shared calendar.',
          benefits: [
            'Company-wide and personal events, together',
            'Custom categories that fit how your company works',
            'Upcoming events surface right on your feed'
          ]
        },
        {
          icon: 'mdi-shield-lock',
          gradient: 'linear-gradient(135deg, #B8923A 0%, #8A6B28 100%)',
          title: 'Privacy & Moderation',
          description: 'Every post and group can be public or private, and admins can moderate content with confidence.',
          benefits: [
            'Public or private visibility on every post and group',
            'Admin tools for announcements and moderation',
            'Role-based access for Members and Admins'
          ]
        },
        {
          icon: 'mdi-shield-check-outline',
          gradient: 'linear-gradient(135deg, #3A3D44 0%, #1F2024 100%)',
          title: 'Admin Tools & Safety',
          description: 'A real-time activity feed, flagged content review, and a full audit log keep the whole platform accountable.',
          benefits: [
            'Activity feed tracks posts and flagged content',
            'Review and act on flagged posts before they\'re a problem',
            'Full audit log and CSV data export'
          ]
        }
      ],
      
      demoTabs: [
        {
          icon: 'mdi-cellphone',
          label: 'Mobile App',
          title: 'Native iOS & Android Apps',
          description: 'Beautiful, intuitive mobile experience designed for everyone at your company, on the go.',
          gradient: 'linear-gradient(135deg, #F0D080 0%, #C9A84C 100%)',
          items: [
            'Clean, modern interface for every user type',
            'Quick messaging for urgent communications',
            'Offline mode for viewing content',
            'Push notifications for likes, comments, and messages'
          ]
        },
        {
          icon: 'mdi-monitor-dashboard',
          label: 'Admin Portal',
          title: 'Tools for HR & Admins',
          description: 'A dashboard for HR and company admins to manage employees, groups, and announcements.',
          gradient: 'linear-gradient(135deg, #6A6D74 0%, #3A3D44 100%)',
          items: [
            'Manage employee accounts and permissions',
            'Content moderation for posts and groups',
            'Announcements with pinning and audience targeting',
            'Group and department organization'
          ]
        },
        {
          icon: 'mdi-web',
          label: 'Web Platform',
          title: 'Full-Featured Web Experience',
          description: 'Access everything from any browser with our responsive web application.',
          gradient: 'linear-gradient(135deg, #9C7A34 0%, #6B5423 100%)',
          items: [
            'Works on any device or screen size',
            'Same features as mobile apps',
            'Easy photo and video uploads',
            'Perfect for desktop administration'
          ]
        },
        {
          icon: 'mdi-cog',
          label: 'Customization',
          title: 'Feels Like Your Company',
          description: 'Light customization so groups, profiles, and recognition feel like part of your company.',
          gradient: 'linear-gradient(135deg, #4A4D54 0%, #2C2E33 100%)',
          items: [
            'Custom cover colors for groups',
            'Personalized profiles with bios and interests',
            'Flexible design that fits any company'
          ]
        }
      ],
      
      platformStats: [
        {
          icon: 'mdi-speedometer',
          value: '<100ms',
          label: 'Average Load Time',
          gradient: 'linear-gradient(135deg, #F0D080 0%, #C9A84C 100%)'
        },
        {
          icon: 'mdi-cloud-upload',
          value: 'Unlimited',
          label: 'Media Storage',
          gradient: 'linear-gradient(135deg, #6A6D74 0%, #3A3D44 100%)'
        },
        {
          icon: 'mdi-account-multiple',
          value: 'Unlimited',
          label: 'User Accounts',
          gradient: 'linear-gradient(135deg, #9C7A34 0%, #6B5423 100%)'
        },
        {
          icon: 'mdi-clock-fast',
          value: '24/7',
          label: 'Support Available',
          gradient: 'linear-gradient(135deg, #4A4D54 0%, #2C2E33 100%)'
        }
      ],
      
      coreBenefits: [
        {
          icon: 'mdi-account-heart',
          gradient: 'linear-gradient(135deg, #F0D080 0%, #C9A84C 100%)',
          badge: 'FOR EMPLOYEES',
          title: 'Connect & Engage',
          description: 'Your company life in one place — recognition, groups, and everything happening at work.',
          points: [
            'Recognize teammates and celebrate wins',
            'Connect with colleagues and make new connections',
            'Join groups and communities you care about',
            'Stay on top of announcements and events'
          ]
        },
        {
          icon: 'mdi-account-tie',
          gradient: 'linear-gradient(135deg, #6A6D74 0%, #3A3D44 100%)',
          badge: 'FOR MANAGERS & TEAM LEADS',
          title: 'Simplify Team Communication',
          description: 'Reach your team in one place instead of juggling email chains and scattered tools.',
          points: [
            'Recognize standout performers publicly',
            'Message your team directly',
            'Share team updates and celebrate wins',
            'Moderate groups and posts with ease'
          ]
        },
        {
          icon: 'mdi-heart-outline',
          gradient: 'linear-gradient(135deg, #9C7A34 0%, #6B5423 100%)',
          badge: 'FOR HR & PEOPLE TEAMS',
          title: 'Track Culture at Scale',
          description: 'Plan announcements and digests, track birthdays and anniversaries, and keep a pulse on engagement.',
          points: [
            'Schedule announcements and digests',
            'Track birthdays, anniversaries, and milestones',
            'See engagement trends across the company',
            'Manage the company calendar'
          ]
        },
        {
          icon: 'mdi-office-building',
          gradient: 'linear-gradient(135deg, #4A4D54 0%, #2C2E33 100%)',
          badge: 'FOR COMPANIES',
          title: 'Bring Your Whole Company Together',
          description: 'Give your whole company one shared space instead of scattered apps, emails, and spreadsheets.',
          points: [
            'One platform for employees, managers, HR, and admins',
            'Admin tools to manage groups and announcements',
            'Public and private spaces for every part of company life',
            'Built-in moderation and privacy controls'
          ]
        }
      ],
      
      techFeatures: [
        {
          icon: 'mdi-eye-lock',
          color: '#C9A84C',
          title: 'Privacy Controls',
          description: 'Every post and group can be set to public or private, giving people control over who sees what.'
        },
        {
          icon: 'mdi-shield-check',
          color: '#9C7A34',
          title: 'Role-Based Access',
          description: 'Employees, managers, HR teams, and admins each see exactly what\'s relevant to them.'
        },
        {
          icon: 'mdi-cloud-lock',
          color: '#6A6D74',
          title: 'Secure Cloud',
          description: 'Enterprise-grade cloud infrastructure with 99.9% uptime guarantee.'
        },
        {
          icon: 'mdi-backup-restore',
          color: '#4A4D54',
          title: 'Auto Backups',
          description: 'Automated daily backups ensure your data is never lost.'
        },
        {
          icon: 'mdi-update',
          color: '#B8923A',
          title: 'Regular Updates',
          description: 'Continuous improvements and new features at your request.'
        },
        {
          icon: 'mdi-cellphone-link',
          color: '#8A6B28',
          title: 'Cross-Platform',
          description: 'Works seamlessly across iOS, Android, and web browsers.'
        },
        {
          icon: 'mdi-face-agent',
          color: '#795548',
          title: 'Responsive Support',
          description: 'Our team is on hand to help when you need it.'
        },
        {
          icon: 'mdi-flag-checkered',
          color: '#3A3D44',
          title: 'Content Moderation',
          description: 'Admin tools to review, pin, and manage posts, groups, and announcements.'
        }
      ],
      
      featureGroups: [
        {
          icon: 'mdi-image-multiple',
          title: 'Social Feed & Recognition',
          features: [
            { name: 'Photo & video posts', detail: 'Share moments from company life' },
            { name: 'Recognition & kudos', detail: 'Recognize colleagues across every category' },
            { name: 'Public or groups-only', detail: 'Choose who sees each post' },
            { name: 'Direct messaging', detail: 'One-on-one and group chats' },
            { name: 'Push notifications', detail: 'Instant alerts for likes, comments, and messages' },
            { name: 'Mobile & web apps', detail: 'Native iOS and Android apps, plus web access' }
          ]
        },
        {
          icon: 'mdi-cake-variant-outline',
          title: 'Celebrations & Milestones',
          features: [
            { name: 'Automatic birthdays', detail: 'Detected and surfaced on the feed' },
            { name: 'Work anniversaries', detail: 'Never miss a milestone' },
            { name: 'Custom milestones', detail: 'Promotions, launches, and team wins' },
            { name: 'Celebrations feed', detail: 'See what\'s coming up across the company' }
          ]
        },
        {
          icon: 'mdi-account-group',
          title: 'Groups & Communities',
          features: [
            { name: 'Public & private groups', detail: 'Create spaces for departments and shared interests' },
            { name: 'Group posts & cover photos', detail: 'Share updates within your group' },
            { name: 'Member management', detail: 'Add, remove, and organize group members' },
            { name: 'Discover public groups', detail: 'Browse and join groups open to the company' }
          ]
        },
        {
          icon: 'mdi-account-multiple',
          title: 'Discovery & Connections',
          features: [
            { name: 'Browse the company', detail: 'Search and filter by role and department' },
            { name: 'Smart suggestions', detail: 'Find new people to connect with' },
            { name: 'Employee profiles', detail: 'Bios, interests, and activities' },
            { name: 'Notifications', detail: 'Stay on top of likes, comments, and messages' }
          ]
        },
        {
          icon: 'mdi-bullhorn-outline',
          title: 'Announcements & Digests',
          features: [
            { name: 'Company-wide announcements', detail: 'Reach everyone at once' },
            { name: 'Scheduled digests', detail: 'A weekly summary of what happened' },
            { name: 'Pinned & targeted', detail: 'Pin announcements to the right audience' },
            { name: 'Company calendar', detail: 'Every event in one shared place' }
          ]
        },
        {
          icon: 'mdi-shield-check',
          title: 'Admin & Safety Tools',
          features: [
            { name: 'Enterprise-grade security', detail: 'Employee privacy protection built-in, AES-256 encryption' },
            { name: 'Content moderation', detail: 'Review and manage posts, groups, and announcements' },
            { name: 'Announcements', detail: 'Pin and target announcements by audience' },
            { name: 'Technical Support', detail: 'Phone, email, and chat support' },
            { name: 'Free onboarding', detail: 'Setup assistance and training' },
            { name: 'Regular updates', detail: 'New features at your request' },
            { name: 'SLA guarantee', detail: '99.9% uptime commitment' }
          ]
        }
      ]
    }),
    
    mounted() {
      setTimeout(() => {
        this.mounted = true;
      }, 100);
      
      this.setupScrollAnimations();
    },
    
    methods: {
      getFloatStyle(index) {
        const positions = [
          { top: '10%', left: '10%' },
          { top: '20%', right: '15%' },
          { bottom: '25%', left: '20%' },
          { bottom: '15%', right: '10%' },
          { top: '50%', left: '50%' }
        ];
        
        return {
          ...positions[index - 1],
          animationDelay: `${index * 0.5}s`
        };
      },
      
      scrollToFeatures() {
        const element = document.getElementById('features');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
      
      scrollToDemo() {
        const element = document.getElementById('demo');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
      
      setupScrollAnimations() {
        const observerOptions = {
          threshold: 0.15,
          rootMargin: '0px 0px -50px 0px'
        };
        
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              if (entry.target.classList.contains('features-section')) {
                this.featuresVisible = true;
              } else if (entry.target.classList.contains('demo-section')) {
                this.demoVisible = true;
              } else if (entry.target.classList.contains('benefits-section')) {
                this.benefitsVisible = true;
              } else if (entry.target.classList.contains('pricing-section')) {
                this.pricingVisible = true;
              }
            }
          });
        }, observerOptions);
        
        setTimeout(() => {
          const sections = document.querySelectorAll('.features-section, .demo-section, .benefits-section, .pricing-section');
          sections.forEach(section => observer.observe(section));
        }, 500);
      }
    }
  };
</script>

<style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@500;600;700;800&family=Cormorant+Garamond:wght@400;500;600&display=swap');

  * {
    box-sizing: border-box;
  }

  .cc-home {
    --bg-page: #08090a;
    --bg-alt: #0f1012;
    --bg-card: #14151a;
    --bg-card-hover: #1a1b21;
    --border-subtle: rgba(255, 255, 255, 0.09);
    --border-gold: rgba(201, 168, 76, 0.35);
    --text-primary: #F5F0E8;
    --text-secondary: rgba(245, 240, 232, 0.68);
    --text-muted: rgba(245, 240, 232, 0.48);
    --gold: #C9A84C;
    --gold-light: #F0D080;
    --gold-dark: #9C7A34;

    overflow-x: hidden;
    background: var(--bg-page);
    padding-top: 80px;
    font-family: 'Syne', -apple-system, BlinkMacSystemFont, sans-serif;
    color: var(--text-primary);
  }

  .cc-home h1,
  .cc-home h2,
  .cc-home h3,
  .cc-home .hero-title,
  .cc-home .section-title,
  .cc-home .cta-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    letter-spacing: -0.005em;
  }

  /* Unified modern gold button, used across hero, pricing, CTAs */
  .btn-gold {
    text-transform: none !important;
    font-weight: 600 !important;
    letter-spacing: 0.01em;
    font-size: 14.5px;
    border-radius: 10px !important;
    background: linear-gradient(150deg, var(--gold-light) 0%, var(--gold) 60%, var(--gold-dark) 100%) !important;
    color: #08090A !important;
    box-shadow: 0 6px 18px rgba(201, 168, 76, 0.28) !important;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .btn-gold:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(201, 168, 76, 0.38) !important;
  }
  
  /* ==================== HERO SECTION ==================== */
  .hero-section {
    position: relative;
    min-height: 92vh;
    background: linear-gradient(160deg, #101114 0%, #08090a 55%, #050506 100%);
    display: flex;
    align-items: center;
    overflow: hidden;
    padding-top: 60px;
  }
  
  .hero-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    pointer-events: none;
  }
  
  .gradient-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(90px);
    opacity: 0.3;
  }
  
  .orb-1 {
    width: 560px;
    height: 560px;
    background: radial-gradient(circle, rgba(201, 168, 76, 0.5) 0%, transparent 70%);
    top: -180px;
    right: -160px;
  }
  
  .hero-content {
    position: relative;
    z-index: 2;
    color: white;
  }

  .hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.85);
    opacity: 0;
    transform: translateY(-8px);
    transition: all 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .hero-eyebrow.animate-in {
    opacity: 1;
    transform: translateY(0);
  }
  
  .hero-title {
    font-size: 3.25rem;
    font-weight: 600;
    line-height: 1.15;
    letter-spacing: -0.02em;
    opacity: 0;
    transform: translateX(-24px);
    transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s;
  }
  
  .hero-title.slide-in-left {
    opacity: 1;
    transform: translateX(0);
  }
  
  .hero-subtitle {
    font-size: 1.15rem;
    line-height: 1.6;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.72);
    opacity: 0;
    transform: translateX(-24px);
    transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s;
  }
  
  .hero-subtitle.slide-in-left-delayed {
    opacity: 0.95;
    transform: translateX(0);
  }
  
  .cta-buttons {
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.8s ease-out 0.6s;
  }
  
  .cta-buttons.fade-in-up {
    opacity: 1;
    transform: translateY(0);
  }
  
  .cta-btn-primary {
    font-size: 16px;
    padding: 26px 34px !important;
  }
  
  .trust-indicators {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    margin-top: 30px;
    opacity: 0;
    transition: all 0.8s ease-out 0.8s;
  }
  
  .trust-indicators.fade-in {
    opacity: 1;
  }
  
  .trust-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.75);
  }
  
  /* Phone Mockup */
  .phone-mockup-container {
    position: relative;
    display: inline-block;
    opacity: 0;
    transform: translateY(50px);
    transition: all 1s ease-out 0.4s;
  }
  
  .phone-mockup-container.float-animation {
    opacity: 1;
    transform: translateY(0);
    animation: gentle-float 6s infinite ease-in-out;
  }
  
  @keyframes gentle-float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-20px);
    }
  }
  
  .mockup-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120%;
    height: 120%;
    background: radial-gradient(circle, rgba(240, 208, 128, 0.25) 0%, transparent 70%);
    filter: blur(40px);
    animation: pulse-glow 4s infinite ease-in-out;
  }
  
  @keyframes pulse-glow {
    0%, 100% {
      opacity: 0.5;
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      opacity: 0.8;
      transform: translate(-50%, -50%) scale(1.1);
    }
  }
  
  .phone-frame {
    position: relative;
    width: 340px;
    height: 690px;
    background: #1a1a1a;
    border-radius: 45px;
    padding: 12px;
    box-shadow: 
      0 0 0 2px #2a2a2a,
      0 0 0 8px #1a1a1a,
      0 30px 80px rgba(0, 0, 0, 0.5);
  }
  
  .phone-screen {
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 35px;
    overflow: hidden;
    position: relative;
  }
  
  .status-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 20px;
    background: linear-gradient(135deg, #C9A84C 0%, #F0D080 100%);
    color: white;
    font-size: 13px;
    font-weight: 600;
    position: relative;
  }
  
  .time {
    flex: 1;
  }
  
  .notch {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 140px;
    height: 22px;
    background: #1a1a1a;
    border-radius: 0 0 20px 20px;
  }
  
  .status-icons {
    display: flex;
    gap: 6px;
    align-items: center;
  }
  
  .app-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    background: linear-gradient(135deg, #C9A84C 0%, #3A3D44 100%);
    color: white;
  }
  
  .header-logo {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 16px;
    font-weight: 700;
  }
  
  .header-actions {
    position: relative;
  }
  
  .notification-dot {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 8px;
    height: 8px;
    background: #C9A84C;
    border-radius: 50%;
    border: 2px solid white;
  }
  
  .screen-content {
    height: calc(100% - 120px);
    overflow-y: auto;
    background: #f8f9fa;
    padding: 16px;
  }
  
  .screen-content::-webkit-scrollbar {
    width: 0;
  }
  
  .stories-row {
    display: flex;
    gap: 16px;
    padding-bottom: 16px;
    overflow-x: auto;
    margin-bottom: 12px;
  }
  
  .stories-row::-webkit-scrollbar {
    display: none;
  }
  
  .story-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    min-width: 70px;
  }
  
  .story-ring {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    padding: 3px;
    background: linear-gradient(135deg, #C9A84C 0%, #3A3D44 100%);
    animation: rotate-gradient 3s linear infinite;
  }
  
  @keyframes rotate-gradient {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  
  .story-avatar {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .story-item span {
    font-size: 11px;
    font-weight: 600;
    color: #495057;
    text-align: center;
  }
  
  .feed-post {
    background: white;
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    margin-bottom: 16px;
  }
  
  .post-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }
  
  .user-info {
    display: flex;
    gap: 12px;
    align-items: center;
  }
  
  .user-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6A6D74 0%, #3A3D44 100%);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .user-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  
  .user-name {
    font-size: 14px;
    font-weight: 700;
    color: #212529;
  }
  
  .post-meta {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #6c757d;
  }
  
  .dot {
    margin: 0 4px;
  }
  
  .post-content {
    margin-bottom: 12px;
  }
  
  .post-content p {
    font-size: 14px;
    color: #212529;
    line-height: 1.5;
    margin: 0;
  }
  
  .post-image {
    margin-bottom: 12px;
    border-radius: 12px;
    overflow: hidden;
  }
  
  .image-placeholder {
    width: 100%;
    height: 200px;
    background: #e9ecef;
  }
  
  .image-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2px;
    height: 100%;
  }
  
  .grid-item {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .grid-item:first-child {
    grid-row: 1 / 3;
  }
  
  .post-stats {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #e9ecef;
    margin-bottom: 8px;
    font-size: 13px;
    color: #6c757d;
  }
  
  .stat-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .reaction-icons {
    display: flex;
    margin-right: 4px;
  }
  
  .reaction-bubble {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    border: 2px solid white;
    margin-left: -6px;
  }
  
  .reaction-bubble:first-child {
    margin-left: 0;
  }
  
  .post-actions {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 4px;
  }
  
  .action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #6c757d;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .action-btn:hover {
    background: #f8f9fa;
  }
  
  .action-btn.active {
    color: #C9A84C;
  }
  
  .bottom-nav {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-around;
    align-items: center;
    padding: 12px 20px 16px;
    background: white;
    border-top: 1px solid #e9ecef;
  }
  
  .nav-item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    color: #6c757d;
    transition: all 0.2s ease;
  }
  
  .nav-item.active {
    color: #C9A84C;
  }
  
  .nav-indicator {
    width: 6px;
    height: 6px;
    background: #C9A84C;
    border-radius: 50%;
    position: absolute;
    bottom: -8px;
  }
  
  .message-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 16px;
    height: 16px;
    background: #C9A84C;
    color: white;
    border-radius: 50%;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
  }
  
  /* ==================== FEATURES SECTION ==================== */
  .features-section {
    padding: 100px 0 80px;
    background: var(--bg-page);
    position: relative;
  }
  
  .section-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 20px;
    background: rgba(201, 168, 76, 0.12);
    border: 1px solid var(--border-gold);
    border-radius: 30px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 1px;
    color: var(--gold-light);
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.6s ease-out;
  }
  
  .section-badge.fade-in-up {
    opacity: 1;
    transform: translateY(0);
  }
  
  .section-title {
    font-size: 3rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--text-primary);
    line-height: 1.2;
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.6s ease-out 0.1s;
  }
  
  .section-title.fade-in-up {
    opacity: 1;
    transform: translateY(0);
  }
  
  .section-subtitle {
    font-size: 1.15rem;
    color: var(--text-secondary);
    max-width: 700px;
    margin: 0 auto;
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.6s ease-out 0.2s;
    font-family: 'Syne', sans-serif;
  }
  
  .section-subtitle.fade-in-up-delayed {
    opacity: 1;
    transform: translateY(0);
  }
  
  .feature-card-modern {
    position: relative;
    background: var(--bg-card);
    border-radius: 18px;
    padding: 40px 32px;
    height: 100%;
    overflow: hidden;
    border: 1px solid var(--border-subtle);
    opacity: 0;
    transform: translateY(30px) scale(0.95);
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  .feature-card-modern.card-animate-in {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  
  .feature-card-modern:hover {
    transform: translateY(-6px);
    background: var(--bg-card-hover);
    border-color: var(--border-gold);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
  }
  
  .feature-card-modern:hover .feature-card-glow {
    opacity: 0.15;
  }
  
  .feature-card-glow {
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }
  
  .feature-card-content {
    position: relative;
    z-index: 2;
  }
  
  .feature-icon-modern {
    position: relative;
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }
  
  .icon-ring {
    position: absolute;
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
    border: 2px solid;
    border-radius: 24px;
    opacity: 0.2;
    border-color: inherit;
    animation: pulse-ring 3s infinite ease-in-out;
  }
  
  @keyframes pulse-ring {
    0%, 100% {
      transform: scale(1);
      opacity: 0.2;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.1;
    }
  }
  
  .feature-title {
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 12px;
    line-height: 1.3;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .feature-description {
    font-size: 1rem;
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 20px;
    font-family: 'Syne', sans-serif;
  }
  
  .feature-benefits {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .benefit-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 0.95rem;
    color: var(--text-secondary);
    line-height: 1.5;
    font-family: 'Syne', sans-serif;
  }
  
  /* ==================== DEMO SECTION ==================== */
  .demo-section {
    padding: 100px 0;
    background: var(--bg-alt);
  }
  
  .demo-interface {
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
    opacity: 0;
    transform: translateX(-50px);
    transition: all 0.8s ease-out;
  }
  
  .demo-interface.slide-in-left {
    opacity: 1;
    transform: translateX(0);
  }
  
  .demo-tabs {
    display: flex;
    background: rgba(255, 255, 255, 0.03);
    padding: 8px;
    gap: 8px;
    border-bottom: 1px solid var(--border-subtle);
  }
  
  .demo-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px 12px;
    background: transparent;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .demo-tab:hover {
    background: rgba(201, 168, 76, 0.1);
    color: var(--gold-light);
  }
  
  .demo-tab.active {
    background: rgba(201, 168, 76, 0.14);
    color: var(--gold-light);
    box-shadow: none;
  }
  
  .demo-content {
    padding: 40px;
  }
  
  .demo-panel {
    animation: fade-in 0.5s ease;
  }
  
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .demo-visual {
    position: relative;
    height: 200px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    overflow: hidden;
  }
  
  .demo-float-elements {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  
  .float-element {
    position: absolute;
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    animation: float-element 4s infinite ease-in-out;
  }
  
  @keyframes float-element {
    0%, 100% {
      transform: translateY(0) translateX(0);
    }
    50% {
      transform: translateY(-20px) translateX(10px);
    }
  }
  
  .demo-panel-title {
    font-size: 1.7rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 12px;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .demo-panel-text {
    font-size: 1.05rem;
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 24px;
    font-family: 'Syne', sans-serif;
  }
  
  .demo-features-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  
  .demo-feature-item {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }
  
  .feature-number {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #F0D080 0%, #C9A84C 100%);
    color: #08090A;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
  }
  
  .feature-text {
    flex: 1;
    padding-top: 4px;
    font-size: 1rem;
    color: var(--text-secondary);
    line-height: 1.6;
    font-family: 'Syne', sans-serif;
  }
  
  .demo-info {
    opacity: 0;
    transform: translateX(50px);
    transition: all 0.8s ease-out;
  }
  
  .demo-info.slide-in-right {
    opacity: 1;
    transform: translateX(0);
  }
  
  .section-text {
    font-size: 1.1rem;
    color: var(--text-secondary);
    line-height: 1.8;
    font-family: 'Syne', sans-serif;
  }
  
  .info-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
  
  .stat-item-modern {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    padding: 24px;
    background: var(--bg-card);
    border-radius: 16px;
    border: 1px solid var(--border-subtle);
    transition: all 0.3s ease;
  }
  
  .stat-item-modern:hover {
    transform: translateY(-4px);
    border-color: var(--border-gold);
  }
  
  .stat-icon {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .stat-details {
    flex: 1;
  }
  
  .stat-value {
    font-size: 1.8rem;
    font-weight: 800;
    color: var(--text-primary);
    line-height: 1;
    margin-bottom: 6px;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .stat-label {
    font-size: 0.95rem;
    color: var(--text-secondary);
    font-weight: 500;
    font-family: 'Syne', sans-serif;
  }
  
  /* ==================== BENEFITS SECTION ==================== */
  .benefits-section {
    position: relative;
    padding: 100px 0;
    background: linear-gradient(160deg, #0f1012 0%, #08090a 100%);
    overflow: hidden;
  }
  
  .benefits-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
  
  .benefit-shape {
    position: absolute;
    border-radius: 50%;
    background: rgba(201, 168, 76, 0.07);
    filter: blur(60px);
  }
  
  .shape-1 {
    width: 500px;
    height: 500px;
    top: -100px;
    left: -100px;
    animation: float-shape 20s infinite ease-in-out;
  }
  
  .shape-2 {
    width: 400px;
    height: 400px;
    bottom: -100px;
    right: -100px;
    animation: float-shape 15s infinite ease-in-out 5s;
  }
  
  .shape-3 {
    width: 300px;
    height: 300px;
    top: 50%;
    left: 50%;
    animation: float-shape 18s infinite ease-in-out 10s;
  }
  
  @keyframes float-shape {
    0%, 100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(50px, -50px);
    }
  }
  
  .benefit-card {
    position: relative;
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 18px;
    padding: 0;
    overflow: hidden;
    opacity: 0;
    transform: translateY(30px) scale(0.95);
    transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  .benefit-card.card-animate-in {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  
  .benefit-card:hover {
    transform: translateY(-6px);
    border-color: var(--border-gold);
  }
  
  .benefit-card-inner {
    padding: 36px;
  }
  
  .benefit-icon-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }
  
  .benefit-icon-wrap {
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }
  
  .benefit-badge {
    padding: 6px 16px;
    background: rgba(201, 168, 76, 0.12);
    border: 1px solid var(--border-gold);
    color: var(--gold-light);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
  }
  
  .benefit-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 12px;
    line-height: 1.3;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .benefit-text {
    font-size: 1.05rem;
    color: var(--text-secondary);
    line-height: 1.7;
    margin-bottom: 20px;
    font-family: 'Syne', sans-serif;
  }
  
  .benefit-points {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .point-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: 0.95rem;
    color: var(--text-secondary);
    line-height: 1.6;
    font-family: 'Syne', sans-serif;
  }
  
  .point-dot {
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    background: linear-gradient(135deg, #F0D080 0%, #C9A84C 100%);
    border-radius: 50%;
    margin-top: 8px;
  }
  
  /* ==================== TECHNOLOGY SECTION ==================== */
  .technology-section {
    padding: 100px 0;
    background: var(--bg-alt);
  }
  
  .tech-card {
    text-align: center;
    padding: 32px 20px;
    transition: all 0.3s ease;
  }
  
  .tech-card:hover {
    transform: translateY(-8px);
  }
  
  .tech-icon {
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }
  
  .tech-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 8px;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .tech-description {
    font-size: 0.95rem;
    color: var(--text-secondary);
    line-height: 1.6;
    font-family: 'Syne', sans-serif;
  }
  
  .tech-certifications {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 32px;
    margin-top: 40px;
    padding: 40px;
    background: var(--bg-card);
    border-radius: 18px;
    border: 1px solid var(--border-subtle);
  }
  
  .cert-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text-secondary);
    font-family: 'Syne', sans-serif;
  }
  
  /* ==================== PRICING SECTION ==================== */
  .pricing-section {
    padding: 100px 0;
    background: var(--bg-page);
  }
  
  .pricing-card-premium {
    position: relative;
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
    opacity: 0;
    transform: scale(0.9);
    transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  .pricing-card-premium.scale-in {
    opacity: 1;
    transform: scale(1);
  }
  
  .pricing-badge {
    position: absolute;
    top: 24px;
    right: 24px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: linear-gradient(135deg, #F0D080 0%, #C9A84C 100%);
    color: #08090A;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    box-shadow: 0 4px 12px rgba(201, 168, 76, 0.4);
  }
  
  .pricing-layout {
    display: grid;
    grid-template-columns: 340px 1fr;
  }

  .pricing-summary {
    background: linear-gradient(160deg, #14151a 0%, #08090a 100%);
    padding: 56px 36px;
    text-align: center;
    color: white;
    border-right: 1px solid var(--border-gold);
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .pricing-icon {
    margin-bottom: 16px;
  }
  
  .pricing-plan-name {
    font-size: 1.6rem;
    font-weight: 700;
    margin-bottom: 8px;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .pricing-tagline {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 24px;
    font-family: 'Syne', sans-serif;
  }

  .pricing-amount-container {
    margin-bottom: 28px;
  }
  
  .pricing-amount-large {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin-bottom: 6px;
  }
  
  .currency {
    font-size: 2rem;
    font-weight: 700;
    margin-right: 4px;
    margin-top: 6px;
    color: var(--gold-light);
  }
  
  .amount {
    font-size: 3.75rem;
    font-weight: 800;
    line-height: 1;
    color: var(--gold-light);
  }

  .amount .cents {
    font-size: 1.75rem;
    font-weight: 700;
    vertical-align: super;
  }

  .pricing-period-label {
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 14px;
    font-family: 'Syne', sans-serif;
  }
  
  .pricing-note {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
    font-family: 'Syne', sans-serif;
  }
  
  .pricing-features-panel {
    padding: 48px 40px;
  }
  
  .feature-groups {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 28px 36px;
  }
  
  .group-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border-subtle);
  }
  
  .group-header h4 {
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .group-features {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .feature-row {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: default;
  }
  
  .feature-name {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--text-secondary);
    line-height: 1.3;
    font-family: 'Syne', sans-serif;
  }
  
  .pricing-guarantees {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    background: rgba(255, 255, 255, 0.04);
    border-radius: 14px;
    border: 1px solid var(--border-subtle);
    width: 100%;
    margin-top: 8px;
  }
  
  .guarantee-item {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.75);
    font-weight: 500;
    font-family: 'Syne', sans-serif;
  }
  
  /* ==================== FINAL CTA SECTION ==================== */
  .final-cta-section {
    position: relative;
    padding: 120px 0;
    background: linear-gradient(160deg, #14151a 0%, #08090a 60%, #050506 100%);
    overflow: hidden;
  }
  
  .cta-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  
  .cta-orb {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(201, 168, 76, 0.25) 0%, transparent 70%);
    filter: blur(60px);
    animation: float-orb 15s infinite ease-in-out;
  }
  
  .cta-orb-1 {
    width: 400px;
    height: 400px;
    top: -100px;
    left: -100px;
  }
  
  .cta-orb-2 {
    width: 500px;
    height: 500px;
    bottom: -150px;
    right: -150px;
    animation-delay: 7s;
  }
  
  .cta-pattern {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.06) 1px, transparent 1px),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
    background-size: 50px 50px;
    opacity: 0.5;
  }
  
  .cta-content {
    position: relative;
    z-index: 2;
  }
  
  .cta-title {
    font-size: 3rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.2;
  }
  
  .cta-subtitle {
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.7);
    max-width: 700px;
    margin: 0 auto 40px;
    line-height: 1.6;
    font-family: 'Syne', sans-serif;
  }
  
  .cta-buttons-final {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
  }
  
  /* ==================== RESPONSIVE STYLES ==================== */
  @media (max-width: 1264px) {
    .hero-title {
      font-size: 3rem;
    }
    
    .section-title {
      font-size: 2.5rem;
    }
    
    .cta-title {
      font-size: 3rem;
    }
  }
  
  @media (max-width: 960px) {
    .pricing-layout {
      grid-template-columns: 1fr;
    }

    .pricing-summary {
      border-right: none;
      border-bottom: 1px solid var(--border-gold);
    }

    .feature-groups {
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }

    .hero-section {
      min-height: auto;
      padding: 80px 0 60px;
    }
    
    .hero-title {
      font-size: 2.5rem;
      text-align: center;
    }
    
    .hero-subtitle {
      font-size: 1.1rem;
      text-align: center;
    }
    
    .cta-buttons {
      justify-content: center;
    }
    
    .trust-indicators {
      justify-content: center;
    }
    
    .phone-frame {
      width: 280px;
      height: 570px;
    }
    
    .section-title {
      font-size: 2rem;
    }
    
    .section-subtitle {
      font-size: 1.1rem;
    }
    
    .features-section,
    .demo-section,
    .benefits-section,
    .technology-section,
    .pricing-section {
      padding: 60px 0;
    }
    
    .final-cta-section {
      padding: 80px 0;
    }
    
    .cta-title {
      font-size: 2.5rem;
    }
    
    .cta-subtitle {
      font-size: 1.2rem;
    }
    
    .info-stats {
      grid-template-columns: 1fr;
    }
    
    .demo-tabs {
      flex-wrap: wrap;
    }
    
    .demo-tab {
      font-size: 12px;
      padding: 12px 8px;
    }
    
    .demo-tab span {
      display: none;
    }
  }
  
  @media (max-width: 600px) {
    .hero-title {
      font-size: 2rem;
    }
    
    .hero-subtitle {
      font-size: 1rem;
    }
    
    .cta-btn-primary {
      font-size: 16px;
      padding: 20px 32px !important;
    }
    
    .trust-indicators {
      gap: 12px;
    }
    
    .trust-badge {
      font-size: 12px;
      padding: 6px 12px;
    }
    
    .section-title {
      font-size: 1.75rem;
    }
    
    .section-subtitle {
      font-size: 1rem;
    }
    
    .feature-card-modern {
      padding: 28px 24px;
    }
    
    .feature-title {
      font-size: 1.3rem;
    }
    
    .demo-content {
      padding: 24px;
    }
    
    .demo-panel-title {
      font-size: 1.5rem;
    }
    
    .benefit-card-inner {
      padding: 28px 24px;
    }
    
    .benefit-title {
      font-size: 1.4rem;
    }
    
    .pricing-summary {
      padding: 40px 24px;
    }
    
    .pricing-plan-name {
      font-size: 1.4rem;
    }
    
    .amount {
      font-size: 3rem;
    }
    
    .pricing-features-panel {
      padding: 32px 24px;
    }

    .feature-groups {
      grid-template-columns: 1fr;
      gap: 24px;
    }
    
    .cta-title {
      font-size: 2rem;
    }
    
    .cta-subtitle {
      font-size: 1.1rem;
    }
    
    .cta-buttons-final {
      flex-direction: column;
    }
  }
  
  /* ==================== UTILITY ANIMATIONS ==================== */
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
  }
  
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
  
  /* ==================== CUSTOM SCROLLBAR ==================== */
  ::-webkit-scrollbar {
    width: 12px;
  }
  
  ::-webkit-scrollbar-track {
    background: #08090a;
  }
  
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #C9A84C 0%, #9C7A34 100%);
    border-radius: 6px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #F0D080 0%, #C9A84C 100%);
  }
</style>